import React, { useEffect, useRef, useState } from "react";
import { Button } from "@penmate/react-shared/ui/button";
import { US_STATES } from "@penmate/react-shared/lib/usStates";
import type { Facility } from "@penmate/react-shared/api/facilities";
import { Label, TextInput } from "./fields";
import { useFacilityTypeahead } from "./useFacilityTypeahead";
import { useKeyboardLayout } from "./keyboardLayout";

// One screen, two exits: pick the facility and continue, or hand the finding
// to us. The pick is an assist, not a gate — the search is already running —
// so Continue is always open; the copy encourages the pick without
// advertising that it can be skipped, and what the user typed travels on as
// a hint for review when nothing matched it.
export const WhereStep = ({
  firstName,
  state,
  initialFacility,
  query,
  onQueryChange,
  onMissChange,
  onKnownFacility,
  onUnknown,
  onChangeState,
}: {
  firstName: string;
  state: string;
  // A facility that arrived with the link: the screen becomes a confirmation.
  initialFacility?: Facility | null;
  // The typed facility text lives with the flow so it survives Back/forward.
  query: string;
  onQueryChange: (query: string) => void;
  // Whether the typeahead currently finds nothing for the typed text.
  onMissChange: (miss: boolean) => void;
  onKnownFacility: (facility: Facility) => void;
  // Continue without a pick, or "Help me find them": the search carries on.
  onUnknown: () => void;
  // The state is named right here, so correcting it should start right here.
  onChangeState: () => void;
}) => {
  const q = query;
  const setQ = onQueryChange;
  const [selected, setSelected] = useState<Facility | null>(initialFacility || null);
  const { facilities, isSearching, hasResults } = useFacilityTypeahead(state, q);
  const input = useRef<HTMLInputElement | null>(null);
  // Typing under the keyboard is a search: the question and the exits step
  // out, and the field with its results takes the whole visible screen.
  // Picking a result (or leaving the field) brings the screen back.
  const keyboard = useKeyboardLayout();
  const searching = keyboard.open && keyboard.typing;
  const clear = () => {
    touched.current = true;
    setSelected(null);
    setQ("");
    if (input.current) input.current.focus();
  };
  // The inbound facility resolves asynchronously and may land after this step
  // mounted; adopt it as long as the user hasn't touched the field themselves.
  const touched = useRef(false);
  useEffect(() => {
    if (initialFacility && !selected && !touched.current) setSelected(initialFacility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFacility && initialFacility.id]);

  const miss = !selected && hasResults && !isSearching && facilities.length === 0;
  useEffect(() => {
    onMissChange(miss);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miss]);
  const stateName = (US_STATES.find((s) => s.abbrev === state) || { name: state }).name;
  const confirming = !!initialFacility && !!selected && selected.id === initialFacility.id;

  return (
    <div className="flex w-full flex-col gap-5" aria-label="Where are they located">
      {!searching && (
      <div>
        <h1 className="text-[28px] font-extrabold leading-[1.15] tracking-[-0.02em] text-foreground">
          {confirming ? (
            <>Is {firstName} at {initialFacility!.name}?</>
          ) : (
            <>Do you know where {firstName} is&nbsp;located?</>
          )}
        </h1>
        <p className="mt-2 text-base leading-relaxed text-muted-foreground">
          {confirming
            ? "If not, change the facility below or choose Help me find them."
            : `Pick their facility in ${stateName} below. If you don't know where they are, choose Help me find them.`}
        </p>
        <button
          type="button"
          className="mt-1 min-h-[44px] text-[15px] font-semibold text-primary"
          onClick={onChangeState}
        >
          Not in {stateName}? Change it
        </button>
      </div>
      )}

      <div>
        <Label htmlFor="pm-search-facility">Facility name</Label>
        {/* Typing here opens a list below: the field goes to the top so the
            list gets the room the keyboard leaves. */}
        <div className="relative">
          <TextInput
            ref={input}
            id="pm-search-facility"
            type="search"
            enterKeyHint="done"
            autoComplete="off"
            data-kb-reveal="top"
            className={selected || q ? "pr-12" : undefined}
            placeholder="Start typing the facility name"
            value={selected ? selected.name : q}
            onChange={(e) => {
              touched.current = true;
              setSelected(null);
              setQ(e.target.value);
            }}
            // The keyboard's ✓ only puts the keyboard away — this is a
            // typeahead, not a form — so the buttons come back into view.
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
          />
          {/* Centred with auto margins: transform utilities don't take inside
              the shadow root. A thumb-sized target, a clear glyph. */}
          {(selected || q) && (
            <button
              type="button"
              aria-label="Clear facility"
              className="absolute inset-y-0 right-1 my-auto flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={clear}
            >
              <svg className="h-7 w-7" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                <circle cx="14" cy="14" r="12" fill="currentColor" opacity="0.15" />
                <path d="M9.5 9.5l9 9M18.5 9.5l-9 9" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
        {!selected && (isSearching || hasResults || facilities.length > 0) && (
          <ul
            className="pm-kb-results mt-2 max-h-56 overflow-y-auto rounded-xl border border-border bg-card shadow-sm"
            aria-label="Facility results"
          >
            {facilities.map((f: Facility) => (
              <li key={f.id} className="border-b border-border last:border-b-0">
                <button
                  type="button"
                  className="min-h-[48px] w-full px-4 py-3 text-left text-[15px] hover:bg-accent"
                  // A tap must not blur the field before it lands: the blur
                  // would fold the search mode away and move this row out
                  // from under the finger. The field lets go once picked.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setSelected(f);
                    if (input.current) input.current.blur();
                  }}
                >
                  {f.name}
                </button>
              </li>
            ))}
            {miss && (
              <li className="px-4 py-3 text-sm text-muted-foreground">
                No match yet for &ldquo;{q.trim()}&rdquo; &mdash; keep going, we&rsquo;re checking the records.
              </li>
            )}
          </ul>
        )}
        {/* The miss is announced from a region that exists before it fills;
            the row itself stays a plain list item. */}
        <p className="sr-only" role="status" aria-live="polite">
          {miss ? `No match yet for ${q.trim()} — keep going, we’re checking the records.` : ""}
        </p>
        {!miss && !selected && (
          <p className="mt-2 text-sm text-muted-foreground">Knowing the facility helps us find them faster.</p>
        )}
      </div>

      {/* While the search has the keyboard, the results list gets the screen
          — unless there is nothing to list: then the way forward stands right
          under the "no match yet" row. The second exit waits for the keyboard. */}
      {(!searching || miss) && (
      <div className="flex flex-col gap-3">
        <Button
          size="lg"
          className="h-[52px] w-full rounded-xl text-[17px] font-semibold"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => (selected ? onKnownFacility(selected) : onUnknown())}
        >
          {confirming ? "Yes, continue" : "Continue"}
        </Button>
        {!searching && (
        <Button
          size="lg"
          variant="outline"
          className="h-[52px] w-full rounded-xl border-2 border-primary text-[17px] font-semibold text-primary hover:bg-primary/5"
          onClick={onUnknown}
        >
          Help me find them
        </Button>
        )}
      </div>
      )}
    </div>
  );
};
